<h1 mat-dialog-title>
  {{ (isEditing ? 'organizationAdmin.users.dialog.editTitle' : 'organizationAdmin.users.dialog.createTitle') | transloco }}
</h1>

<mat-dialog-content [formGroup]="form">
  <section class="dialog-section">
    <div class="form-row">
      <div class="field-group">
        <span class="field-label">{{ 'dataManagement.organizationSettings.fields.firstName' | transloco }} *</span>
        <mat-form-field appearance="outline" subscriptSizing="dynamic">
          <input matInput formControlName="firstName" [attr.aria-label]="'dataManagement.organizationSettings.fields.firstName' | transloco" />
          @if (form.controls.firstName.hasError('required')) {
            <mat-error>{{ 'dataManagement.userData.userDialog.validation.required' | transloco }}</mat-error>
          }
        </mat-form-field>
      </div>

      <div class="field-group">
        <span class="field-label">{{ 'dataManagement.organizationSettings.fields.lastName' | transloco }} *</span>
        <mat-form-field appearance="outline" subscriptSizing="dynamic">
          <input matInput formControlName="lastName" [attr.aria-label]="'dataManagement.organizationSettings.fields.lastName' | transloco" />
          @if (form.controls.lastName.hasError('required')) {
            <mat-error>{{ 'dataManagement.userData.userDialog.validation.required' | transloco }}</mat-error>
          }
        </mat-form-field>
      </div>
    </div>

    <div class="field-group">
      <span class="field-label">
        {{ 'organizationAdmin.users.dialog.fields.email' | transloco }} *
        @if (!isEditing && !needsPasswordField) {
          ({{ 'organizationAdmin.users.dialog.ssoFootnote' | transloco }})
        }
      </span>
      <mat-form-field appearance="outline" subscriptSizing="dynamic">
        <input matInput formControlName="email" [attr.aria-label]="'organizationAdmin.users.dialog.fields.email' | transloco" />
        @if (data.isSelf) {
          <mat-hint>{{ 'organizationAdmin.users.dialog.ownAccountHint' | transloco }}</mat-hint>
        } @else if (form.controls.email.disabled && !data.user?.isPasswordLoginAllowed) {
          <mat-hint>{{ 'myProfile.personalData.emailHelpSso' | transloco }}</mat-hint>
        }
        @if (form.controls.email.hasError('required')) {
          <mat-error>{{ 'dataManagement.userData.userDialog.validation.required' | transloco }}</mat-error>
        } @else if (form.controls.email.hasError('email')) {
          <mat-error>{{ 'dataManagement.userData.userDialog.validation.email' | transloco }}</mat-error>
        } @else if (form.controls.email.hasError('emailAlreadyUsed')) {
          <mat-error>{{ 'organizationAdmin.users.dialog.fields.emailAlreadyUsed' | transloco }}</mat-error>
        }
      </mat-form-field>
    </div>

    @if (needsPasswordField) {
      <app-password-form formControlName="password" [submitted]="submitted" [showFieldLabels]="true"></app-password-form>
    }
  </section>

  <section class="dialog-section">
    <span class="dialog-section-label">
      {{ 'organizationAdmin.users.dialog.sections.groups' | transloco }}
      <mat-icon class="section-label-hint" [matTooltip]="'organizationAdmin.users.dialog.groupsMergeHint' | transloco">info</mat-icon>
    </span>

    <mat-chip-listbox multiple [attr.aria-label]="'organizationAdmin.users.dialog.sections.groups' | transloco">
      @for (group of data.allGroups; track group.id) {
        <mat-chip-option [selected]="selectedGroupIds().has(group.id)" (selectionChange)="setGroupSelected(group, $event.selected)">{{ group.name }}</mat-chip-option>
      }
    </mat-chip-listbox>

    @if (!hasRestrictedGroup()) {
      <div class="info-callout">
        <mat-icon>info</mat-icon>
        <span>{{ 'organizationAdmin.users.dialog.noGroupHint' | transloco }}</span>
      </div>
    }
  </section>

  <section class="dialog-section">
    <span class="dialog-section-label">{{ 'organizationAdmin.users.dialog.sections.rights' | transloco }}</span>

    <div class="rights-row">
      <div class="rights-row-icon">
        <mat-icon>workspace_premium</mat-icon>
      </div>
      <div class="rights-row-text">
        <strong>{{ 'organizationAdmin.users.dialog.fields.premium' | transloco }}</strong>
        <span class="rights-hint" [class.warn]="isPremiumBlocked()">
          {{ (isPremiumBlocked() ? 'organizationAdmin.users.dialog.fields.premiumLimitReached' : 'organizationAdmin.users.dialog.fields.premiumDescription') | transloco }}
        </span>
      </div>
      <mat-slide-toggle
        color="accent"
        [attr.aria-label]="'organizationAdmin.users.dialog.fields.premium' | transloco"
        [checked]="isPremiumUser()"
        [disabled]="isPremiumBlocked()"
        (change)="isPremiumUser.set($event.checked)">
      </mat-slide-toggle>
    </div>

    <div class="rights-row" [class.disabled]="isAdminToggleLocked()">
      <div class="rights-row-icon">
        <mat-icon>admin_panel_settings</mat-icon>
      </div>
      <div class="rights-row-text">
        <strong>{{ 'organizationAdmin.users.dialog.fields.admin' | transloco }}</strong>
        <span class="rights-hint">
          {{ (isAdminToggleLocked() ? 'organizationAdmin.users.dialog.fields.adminHint' : 'organizationAdmin.users.dialog.fields.adminDescription') | transloco }}
        </span>
      </div>
      <mat-slide-toggle
        [attr.aria-label]="'organizationAdmin.users.dialog.fields.admin' | transloco"
        [checked]="role() !== AdminUserRole.Member"
        [disabled]="isAdminToggleLocked()"
        (change)="toggleAdmin($event.checked)">
      </mat-slide-toggle>
    </div>
  </section>
</mat-dialog-content>

<mat-dialog-actions>
  <span class="dialog-actions-spacer"></span>
  <button mat-button color="primary" class="link-button" type="button" [disabled]="isSaving()" (click)="closeDialog()">
    <mat-icon>close</mat-icon>
    {{ 'common.buttons.cancel' | transloco }}
  </button>
  <button mat-flat-button color="primary" type="button" [disabled]="!hasChanges() || isSaving()" (click)="save()">
    <mat-icon>check</mat-icon>
    {{ (isEditing ? 'common.buttons.save' : 'organizationAdmin.users.toolbar.createUser') | transloco }}
  </button>
</mat-dialog-actions>
